import { useEffect, useCallback } from 'react';
import { useAuth } from 'src/context/AuthContext';
import { sessionsService, SessionData, SessionStats } from 'src/services/sessionsService';
import { useTutorSessionsStore } from 'src/store/tutorSessionsStore';

interface UseTutorSessionsReturn {
  // Data from store
  upcomingSessions: SessionData[];
  pastSessions: SessionData[];
  allSessions: SessionData[];
  
  // Loading states from store
  isLoading: boolean;
  isLoadingUpcoming: boolean;
  isLoadingPast: boolean;
  
  // Error states from store
  error: string | null;
  upcomingError: string | null;
  pastError: string | null;
  
  // Actions
  refreshSessions: () => Promise<void>;
  refreshUpcoming: () => Promise<void>;
  refreshPast: () => Promise<void>;
  getSessionById: (sessionId: string) => Promise<SessionData | null>;
}

export const useTutorSessions = (): UseTutorSessionsReturn => {
  const { user } = useAuth();
  
  // Get state and actions from Zustand store
  const {
    upcomingSessions,
    pastSessions,
    allSessions,
    isLoadingUpcoming,
    isLoadingPast,
    isLoadingAll,
    upcomingError,
    pastError,
    allError,
    setUpcomingSessions,
    setPastSessions,
    setAllSessions,
    setLoadingUpcoming,
    setLoadingPast,
    setLoadingAll,
    setUpcomingError,
    setPastError,
    setAllError,
  } = useTutorSessionsStore();
  
  // Computed loading state
  const isLoading = isLoadingUpcoming || isLoadingPast || isLoadingAll;
  
  // Computed error state
  const error = upcomingError || pastError || allError;

  /**
   * Fetch upcoming sessions for tutor
   */
  const fetchUpcomingSessions = useCallback(async () => {
    if (!user?.id) return;
    
    setLoadingUpcoming(true);
    setUpcomingError(null);
    
    try {
      const sessions = await sessionsService.fetchTutorUpcomingSessions(user.id);
      setUpcomingSessions(sessions);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch upcoming sessions';
      setUpcomingError(errorMessage);
      console.error('Error fetching tutor upcoming sessions:', err);
    } finally {
      setLoadingUpcoming(false);
    }
  }, [user?.id, setLoadingUpcoming, setUpcomingError, setUpcomingSessions]);

  /**
   * Fetch past sessions for tutor
   */
  const fetchPastSessions = useCallback(async () => {
    if (!user?.id) return;
    
    setLoadingPast(true);
    setPastError(null);
    
    try {
      const sessions = await sessionsService.fetchTutorPastSessions(user.id);
      setPastSessions(sessions);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch past sessions';
      setPastError(errorMessage);
      console.error('Error fetching tutor past sessions:', err);
    } finally {
      setLoadingPast(false);
    }
  }, [user?.id, setLoadingPast, setPastError, setPastSessions]);

  /**
   * Fetch all sessions for tutor
   */
  const fetchAllSessions = useCallback(async () => {
    if (!user?.id) return;
    
    setLoadingAll(true);
    setAllError(null);
    
    try {
      const sessions = await sessionsService.fetchTutorSessions(user.id);
      setAllSessions(sessions);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch all sessions';
      setAllError(errorMessage);
      console.error('Error fetching all tutor sessions:', err);
    } finally {
      setLoadingAll(false);
    }
  }, [user?.id, setLoadingAll, setAllError, setAllSessions]);

  /**
   * Refresh all session data
   */
  const refreshSessions = useCallback(async () => {
    await Promise.all([
      fetchUpcomingSessions(),
      fetchPastSessions(),
      fetchAllSessions()
    ]);
  }, [fetchUpcomingSessions, fetchPastSessions, fetchAllSessions]);

  /**
   * Get session by ID
   */
  const getSessionById = useCallback(async (sessionId: string): Promise<SessionData | null> => {
    try {
      return await sessionsService.getSessionById(sessionId);
    } catch (err) {
      console.error('Error fetching session by ID:', err);
      return null;
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    if (user?.id) {
      refreshSessions();
    }
  }, [user?.id, refreshSessions]);

  return {
    // Data
    upcomingSessions,
    pastSessions,
    allSessions,
    
    // Loading states
    isLoading,
    isLoadingUpcoming,
    isLoadingPast,
    
    // Error states
    error,
    upcomingError,
    pastError,
    
    // Actions
    refreshSessions,
    refreshUpcoming: fetchUpcomingSessions,
    refreshPast: fetchPastSessions,
    getSessionById
  };
};